import React, { useState, useRef } from 'react';
import { tickets } from './Register';

export default function TicketsPage() {
  const initialTicketType = new URLSearchParams(window.location.search).get('ticket');
  const initialTicket = tickets.find((ticket) => ticket.type === initialTicketType) || tickets[0];
  const [selectedTicket, setSelectedTicket] = useState(initialTicket);
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [reference, setReference] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: 1,
  });
  const pollRef = useRef(null);

  const total = selectedTicket.amount * Number(form.quantity || 1);

  const updateForm = (e) => {
    const { name, value } = e.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const goHome = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollStatus = (ref) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/payment/status/${ref}`);
        const data = await res.json();
        if (data.status === 'SUCCESSFUL') {
          stopPolling();
          setStatus('success');
        } else if (data.status === 'FAILED') {
          stopPolling();
          setErrorMsg('Payment was declined or timed out. Please try again.');
          setStatus('error');
        }
      } catch {
        stopPolling();
        setErrorMsg('Could not reach the payment server. Please try again.');
        setStatus('error');
      }
    }, 3000);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    setStatus('processing');
    setErrorMsg('');

    try {
      const res = await fetch('/api/payment/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          phone: form.phone,
          name: form.name,
          email: form.email,
          ticketType: selectedTicket.type,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
      }

      setReference(data.reference);
      setStatus('pending');
      pollStatus(data.reference);
    } catch (err) {
      setErrorMsg(err.message || 'Payment initiation failed. Please try again.');
      setStatus('error');
    }
  };

  return (
    <main className="tickets-page">
      <div className="container">
        <button className="tickets-page__back" type="button" onClick={goHome}>← Back to event</button>

        <div className="tickets-page__header">
          <div>
            <div className="checkout-card__eyebrow">LinkedIn Tour Cameroon</div>
            <h1 className="tickets-page__title">Get your ticket</h1>
          </div>
          <p className="tickets-page__desc">
            Complete your purchase using MTN Mobile Money or Orange Money. A secure payment prompt will be sent to your phone.
          </p>
        </div>

        <div className="tickets-page__layout">
          <aside className="tickets-page__plans">
            {tickets.map((ticket) => (
              <button
                key={ticket.type}
                type="button"
                className={`tickets-page__plan${selectedTicket.type === ticket.type ? ' tickets-page__plan--active' : ''}`}
                onClick={() => setSelectedTicket(ticket)}
              >
                <span>
                  <strong>{ticket.type}</strong>
                  {ticket.tag && <small>{ticket.tag}</small>}
                  {ticket.note && <small>{ticket.note}</small>}
                </span>
                <b>{ticket.price}</b>
              </button>
            ))}
          </aside>

          <div className="checkout-card checkout-card--page">
            <div className="checkout-card__summary">
              <div>
                <div className="checkout-card__eyebrow">Secure checkout</div>
                <h3 className="checkout-card__title">Payment details</h3>
                <p className="checkout-card__desc">
                  Enter your buyer information and choose your preferred mobile money provider.
                </p>
              </div>

              <div className="checkout-summary-box">
                <div>
                  <span>Ticket</span>
                  <strong>{selectedTicket.type}</strong>
                </div>
                <div>
                  <span>Quantity</span>
                  <strong>{form.quantity}</strong>
                </div>
                <div>
                  <span>Total</span>
                  <strong>XAF {total.toLocaleString()}</strong>
                </div>
              </div>
            </div>

            <form className="checkout-form" onSubmit={submitPayment}>
              <div className="checkout-form__row">
                <label>
                  Full name
                  <input name="name" value={form.name} onChange={updateForm} required placeholder="Your full name" />
                </label>
                <label>
                  Email address
                  <input name="email" type="email" value={form.email} onChange={updateForm} required placeholder="you@example.com" />
                </label>
              </div>

              <div className="checkout-form__row">
                <label>
                  Mobile money number
                  <input name="phone" value={form.phone} onChange={updateForm} required placeholder="6XX XXX XXX" />
                </label>
                <label>
                  Quantity
                  <select name="quantity" value={form.quantity} onChange={updateForm}>
                    {[1, 2, 3, 4, 5].map((qty) => (
                      <option key={qty} value={qty}>{qty}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="payment-methods">
                <button type="button" className={`payment-method${paymentMethod === 'momo' ? ' payment-method--active' : ''}`} onClick={() => setPaymentMethod('momo')}>
                  <span className="payment-method__badge payment-method__badge--momo">MTN</span>
                  <span>
                    <strong>MTN MoMo</strong>
                    <small>Pay with Mobile Money</small>
                  </span>
                </button>
                <button type="button" className={`payment-method${paymentMethod === 'om' ? ' payment-method--active' : ''}`} onClick={() => setPaymentMethod('om')}>
                  <span className="payment-method__badge payment-method__badge--om">OM</span>
                  <span>
                    <strong>Orange Money</strong>
                    <small>Pay with Orange Money</small>
                  </span>
                </button>
              </div>

              <button
                className="checkout-submit"
                type="submit"
                disabled={status === 'processing' || status === 'pending' || status === 'success'}
              >
                {status === 'processing' && 'Sending payment prompt…'}
                {status === 'pending' && 'Waiting for confirmation on your phone…'}
                {status === 'success' && '✓ Payment confirmed'}
                {(status === 'idle' || status === 'error') && `Pay XAF ${total.toLocaleString()} ↗`}
              </button>

              {status === 'pending' && (
                <div className="checkout-success" style={{ background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.3)', color: '#1e40af' }}>
                  A payment prompt has been sent to <strong>{form.phone}</strong>.
                  Please open your mobile money app or dial the USSD code and confirm.
                  {reference && <div style={{ marginTop: '6px', fontSize: '11px', opacity: 0.7 }}>Ref: {reference}</div>}
                </div>
              )}

              {status === 'success' && (
                <div className="checkout-success">
                  ✓ Payment received! Check your email ({form.email}) for your ticket confirmation.
                </div>
              )}

              {status === 'error' && (
                <div className="checkout-success" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#991b1b' }}>
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}
